import React from 'react';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import type { TabId } from './types.tsx';

const styles = cssMap({
	tabStyles: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		borderRadius: token('radius.xsmall'),
		color: token('color.text.subtle'),
		height: '26px',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		paddingTop: token('space.0'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.150'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: token('color.text.subtle'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			// color: token('color.text.link'),
		},
		'&:focus': {
			outlineOffset: token('space.0'),
		},
		'&:disabled': {
			backgroundColor: token('color.background.disabled'),
			color: token('color.text.disabled'),
		},
	},
	tabSelectedStyles: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.selected'),
		borderRadius: token('radius.xsmall'),
		height: '26px',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		paddingTop: token('space.0'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.150'),
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			// color: token('color.text.subtle'),
		},
		'&:focus': {
			outlineOffset: token('space.0'),
		},
		'&:disabled': {
			backgroundColor: token('color.background.disabled'),
			color: token('color.text.disabled'),
		},
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		cursor: 'default',
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
			color: token('color.text.selected'),
		},
	},
});

type TabProps = {
	id: TabId;
	name: string;
	selected: TabId;
	onTabClick: (analyticsEvent: UIAnalyticsEvent, newSelectedTab: TabId) => void;
};

const TabButton = ({ id, selected, onTabClick, name }: TabProps): React.ReactElement => {
	const isSelected = selected === id;

	return (
		<Pressable
			role="radio"
			xcss={isSelected ? styles.tabSelectedStyles : styles.tabStyles}
			onClick={(event, analyticsEvent) => onTabClick(analyticsEvent, id)}
			aria-checked={isSelected}
			aria-label={name}
		>
			{name}
		</Pressable>
	);
};

export default TabButton;
